import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'components/core/theme-provider';
import { cn } from 'lib/utils';
import { useToast } from 'hooks/use-toast';
import { useUpdateKnowledgeBase } from 'features/profile/hooks/use-knowledge-base';
import type { KnowledgeBaseFormData } from 'features/profile/types/knowledge-base';
import { Switch } from 'components/ui/switch';
import { AccountRow } from './account-row';
import {
  accountFieldClassName,
  accountGhostButtonClassName,
  accountPrimaryButtonClassName,
  accountSectionClassName,
  accountSectionTitleClassName,
} from './account-styles';

// Figma 10596:4340. Name, profession, trait, About you and the new-chat switch are saved
// through the knowledge base API; tone, length, reply language and instructions have no API yet
// (UI commented out below).
export const PERSONALIZATION_TEXT_LIMIT = 1500;

/*
const TONE_OPTIONS = [
  { value: 'friendly', labelKey: 'ACCOUNT_TONE_FRIENDLY', defaultLabel: 'Friendly' },
  { value: 'professional', labelKey: 'ACCOUNT_TONE_PROFESSIONAL', defaultLabel: 'Professional' },
  { value: 'direct', labelKey: 'ACCOUNT_TONE_DIRECT', defaultLabel: 'Direct' },
];

const LENGTH_OPTIONS = [
  { value: 'short', labelKey: 'ACCOUNT_LENGTH_SHORT', defaultLabel: 'Short' },
  { value: 'balanced', labelKey: 'ACCOUNT_LENGTH_BALANCED', defaultLabel: 'Balanced' },
  { value: 'detailed', labelKey: 'ACCOUNT_LENGTH_DETAILED', defaultLabel: 'Detailed' },
];

const REPLY_LANGUAGE_OPTIONS = [
  { value: 'same', labelKey: 'ACCOUNT_REPLY_LANGUAGE_SAME', defaultLabel: 'Same as my message' },
  { value: 'en', labelKey: 'LANGUAGE_EN_US', defaultLabel: 'English' },
  { value: 'de', labelKey: 'LANGUAGE_DE_DE', defaultLabel: 'German' },
  { value: 'fr', labelKey: 'LANGUAGE_FR_FR', defaultLabel: 'French' },
];
*/

type BuddyTalkSettings = {
  nickname: string;
  profession: string;
  trait: string;
  aboutYou: string;
  isUsedInNewChats: boolean;
};

const DEFAULT_SETTINGS: BuddyTalkSettings = {
  nickname: '',
  profession: '',
  trait: '',
  aboutYou: '',
  isUsedInNewChats: true,
};

interface LongTextFieldProps {
  title: string;
  hint: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  titleColor: string;
  mutedColor: string;
  isLast?: boolean;
}

function LongTextField({
  title,
  hint,
  placeholder,
  value,
  onChange,
  titleColor,
  mutedColor,
  isLast,
}: LongTextFieldProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-1.5 py-3.5',
        !isLast && 'border-b border-grey-100 dark:border-border'
      )}
    >
      <p className={cn('text-sm font-medium leading-body', titleColor)}>{title}</p>
      <p className={cn('text-xs leading-heading', mutedColor)}>{hint}</p>
      <textarea
        aria-label={title}
        value={value}
        maxLength={PERSONALIZATION_TEXT_LIMIT}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          'mt-1 min-h-20 w-full resize-y rounded-xl border border-grey-100 bg-true-white px-3 py-2.5 text-sm outline-none placeholder:text-grey-500 focus:border-grey-200 dark:border-border dark:bg-card',
          titleColor
        )}
      />
      <p className={cn('self-end text-xs leading-heading', mutedColor)}>
        {value.length} / {PERSONALIZATION_TEXT_LIMIT}
      </p>
    </div>
  );
}

const toSettings = (knowledgeBase: KnowledgeBaseFormData | null): BuddyTalkSettings => ({
  ...DEFAULT_SETTINGS,
  nickname: knowledgeBase?.user_nickname ?? '',
  profession: knowledgeBase?.user_profession ?? '',
  trait: knowledgeBase?.llm_personality ?? '',
  aboutYou: knowledgeBase?.description ?? '',
  isUsedInNewChats: knowledgeBase?.is_enabled ?? true,
});

interface BuddyTalkCardProps {
  knowledgeBase: KnowledgeBaseFormData | null;
}

/** "How Buddy talks to you" card, on the same knowledge base API as the Settings dialog. */
export function BuddyTalkCard({ knowledgeBase }: BuddyTalkCardProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { toast } = useToast();
  const { mutateAsync: updateKnowledgeBase, isPending: isSaving } = useUpdateKnowledgeBase();
  const [saved, setSaved] = useState(() => toSettings(knowledgeBase));
  const [draft, setDraft] = useState(saved);
  const hasChanges = JSON.stringify(saved) !== JSON.stringify(draft);

  const titleColor = theme === 'dark' ? 'text-true-white' : 'text-grey-950';
  const mutedColor = theme === 'dark' ? 'text-grey-300' : 'text-grey-500';

  const handleSave = async () => {
    try {
      const result = await updateKnowledgeBase({
        user_nickname: draft.nickname,
        user_profession: draft.profession,
        llm_personality: draft.trait,
        description: draft.aboutYou,
        is_enabled: draft.isUsedInNewChats,
      });
      // Same rule as the Settings dialog: an empty response means the save failed.
      if (!result) throw new Error('Knowledge base was not saved');
      setSaved(draft);
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('KNOWLEDGE_BASE_UPDATE_SUCCESS'),
      });
    } catch {
      toast({
        variant: 'destructive',
        title: t('ERROR'),
        description: t('KNOWLEDGE_BASE_UPDATE_ERROR'),
      });
    }
  };

  const updateDraft = (changes: Partial<BuddyTalkSettings>) => setDraft({ ...draft, ...changes });

  const nicknameLabel = t('ACCOUNT_BUDDY_NICKNAME', {
    defaultValue: 'What should Buddy call you?',
  });
  const professionLabel = t('ACCOUNT_PROFESSION', { defaultValue: 'What is your profession?' });
  const traitLabel = t('ACCOUNT_BUDDY_TRAIT', { defaultValue: 'What trait should Buddy have?' });
  const newChatsLabel = t('ACCOUNT_USE_IN_NEW_CHATS', {
    defaultValue: 'Use this in new chats',
  });

  return (
    <section className={accountSectionClassName}>
      <div className="flex flex-col gap-0.5 px-6 pb-1 pt-5">
        <h2 className={accountSectionTitleClassName}>
          {t('ACCOUNT_HOW_BUDDY_TALKS', { defaultValue: 'How Buddy talks to you' })}
        </h2>
        <p className={cn('text-sm', mutedColor)}>
          {t('ACCOUNT_HOW_BUDDY_TALKS_HINT', {
            defaultValue:
              'Your agents follow this too, unless their own instructions say otherwise.',
          })}
        </p>
      </div>

      <div className="px-6 pb-2">
        <AccountRow title={nicknameLabel}>
          <input
            aria-label={nicknameLabel}
            value={draft.nickname}
            onChange={(event) => updateDraft({ nickname: event.target.value })}
            className={cn(accountFieldClassName, 'outline-none focus:border-grey-200')}
          />
        </AccountRow>

        <AccountRow title={professionLabel}>
          <input
            aria-label={professionLabel}
            value={draft.profession}
            placeholder={t('ACCOUNT_PROFESSION_PLACEHOLDER', {
              defaultValue: 'For example: Operations lead',
            })}
            onChange={(event) => updateDraft({ profession: event.target.value })}
            className={cn(
              accountFieldClassName,
              'outline-none placeholder:text-grey-500 focus:border-grey-200'
            )}
          />
        </AccountRow>

        <AccountRow title={traitLabel}>
          <input
            aria-label={traitLabel}
            value={draft.trait}
            placeholder={t('ACCOUNT_BUDDY_TRAIT_PLACEHOLDER', {
              defaultValue: 'For example: Patient and to the point',
            })}
            onChange={(event) => updateDraft({ trait: event.target.value })}
            className={cn(
              accountFieldClassName,
              'outline-none placeholder:text-grey-500 focus:border-grey-200'
            )}
          />
        </AccountRow>

        {/* No API yet — Tone / Length of replies / Reply language
        <AccountRow title={t('ACCOUNT_TONE', { defaultValue: 'Tone' })}>
          <ChoicePills ... />
        </AccountRow>
        <AccountRow title={t('ACCOUNT_REPLY_LENGTH', { defaultValue: 'Length of replies' })}>
          <ChoicePills ... />
        </AccountRow>
        <AccountRow title={replyLanguageLabel}>
          <Select ... />
        </AccountRow>
        */}

        <LongTextField
          title={t('ACCOUNT_ABOUT_YOU', { defaultValue: 'About you' })}
          hint={t('ACCOUNT_ABOUT_YOU_HINT', {
            defaultValue: 'What Buddy should know about you and your work',
          })}
          placeholder={t('ACCOUNT_ABOUT_YOU_PLACEHOLDER', {
            defaultValue: 'For example: I manage a sales team of 12.',
          })}
          value={draft.aboutYou}
          onChange={(aboutYou) => updateDraft({ aboutYou })}
          titleColor={titleColor}
          mutedColor={mutedColor}
        />

        {/* No API yet — Your instructions
        <LongTextField
          title={t('ACCOUNT_YOUR_INSTRUCTIONS', { defaultValue: 'Your instructions' })}
          hint={t('ACCOUNT_YOUR_INSTRUCTIONS_HINT', {
            defaultValue: 'Anything Buddy should always do',
          })}
          placeholder={t('ACCOUNT_YOUR_INSTRUCTIONS_PLACEHOLDER', {
            defaultValue: 'For example: Start long answers with a short summary.',
          })}
          value={draft.instructions}
          onChange={(instructions) => updateDraft({ instructions })}
        />
        */}

        <div className="flex items-center justify-between gap-3 py-3.5">
          <div className="flex min-w-0 flex-col gap-0.5">
            <p className={cn('text-sm font-medium leading-body', titleColor)}>{newChatsLabel}</p>
            <p className={cn('text-xs leading-heading', mutedColor)}>
              {t('ACCOUNT_USE_IN_NEW_CHATS_HINT', {
                defaultValue: 'Buddy starts every new chat with these settings',
              })}
            </p>
          </div>
          <Switch
            aria-label={newChatsLabel}
            checked={draft.isUsedInNewChats}
            onCheckedChange={(isUsedInNewChats) => updateDraft({ isUsedInNewChats })}
            className="data-[state=checked]:bg-grey-900"
          />
        </div>
      </div>

      {/* Cancel and Save only appear once something has changed. */}
      {hasChanges ? (
        <div className="flex items-center justify-end gap-2 border-t border-grey-100 px-6 py-3 dark:border-border">
          <button
            type="button"
            onClick={() => setDraft(saved)}
            disabled={isSaving}
            className={accountGhostButtonClassName}
          >
            {t('ACCOUNT_CANCEL', { defaultValue: 'Cancel' })}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className={accountPrimaryButtonClassName}
          >
            {t('ACCOUNT_SAVE_CHANGES', { defaultValue: 'Save changes' })}
          </button>
        </div>
      ) : null}
    </section>
  );
}
